import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity as ActivityIcon, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { ActivityFeedItem } from './ActivityFeedItem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Build an activity item with the SAME shape as GET /api/activity returns,
 * from a WebSocket `ticket_update status=used` payload.
 * Kept here (not in a new util) because it is the only live-→feed adapter.
 */
function buildPassUsedItemFromWs(payload, eventTitle) {
  const guest = payload.guest_display_name || payload.guest_name || payload.guest_email || 'Guest';
  const method = payload.check_in_method === 'manual' ? 'manual' : 'scan';
  return {
    id: `pass_used-${payload.ticket_id}`,
    kind: 'pass_used',
    at: payload.scanned_at,
    event_id: payload.event_id || null,
    event_title: eventTitle || '',
    actor_name: payload.scanned_by || null,
    summary: `${guest} checked in (${method})`,
    meta: {
      ticket_id: payload.ticket_id,
      method,
      tier: payload.pass_tier_name,
      guest_name: guest,
    },
    link: `/ticket/${payload.ticket_id}`,
  };
}

/**
 * ActivityFeedCard — compact dashboard card showing the last N activity items.
 * Props:
 *   - eventId?: string      (scope to single event; omit for global/host-wide)
 *   - limit?: number        (default 10)
 *   - title?: string
 *   - viewAllHref?: string  (default '/activity', or event-scoped when eventId set)
 *
 * When `eventId` is set AND the REST load succeeds, the card also subscribes to
 * the existing /api/events/{eventId}/live WebSocket and prepends a `pass_used`
 * item when a check-in is broadcast. REST remains the canonical source; WS is a
 * UI nudge only. On refresh/reload, the card re-fetches from REST (ids match →
 * no duplicates).
 */
export default function ActivityFeedCard({ eventId, limit = 10, title = 'Recent Activity', viewAllHref, defaultCollapsed = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(!defaultCollapsed);
  const wsRef = useRef(null);
  const eventTitleRef = useRef('');

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const params = { limit };
      if (eventId) params.event_id = eventId;
      const res = await axios.get(`${API}/activity`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetched = res.data?.items || [];
      setItems(fetched);
      // Cache the event title so live WS items render with the same column
      if (eventId && fetched.length > 0 && fetched[0].event_title) {
        eventTitleRef.current = fetched[0].event_title;
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load activity');
    } finally {
      setLoading(false);
    }
  }, [eventId, limit]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // Live nudge: only for event-scoped feeds, only for pass_used
  useEffect(() => {
    if (!eventId || loading || error) return;

    const token = localStorage.getItem('session_token');
    if (!token || !BACKEND_URL) return;
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

    let closed = false;
    let reconnectTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(`${wsUrl}/api/events/${eventId}/live?token=${token}`);
        wsRef.current = ws;

        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            // Only care about pass_used signals for this event
            if (data.type !== 'ticket_update' || data.status !== 'used') return;
            if (!data.ticket_id || !data.scanned_at) return;

            const enriched = { ...data, event_id: eventId };
            const newItem = buildPassUsedItemFromWs(enriched, eventTitleRef.current);

            setItems((prev) => {
              // Stable dedupe by id — matches what REST will return on refresh
              if (prev.some((it) => it.id === newItem.id)) return prev;
              return [newItem, ...prev];
            });
          } catch {
            /* ignore malformed frames */
          }
        };

        ws.onclose = () => {
          if (closed) return;
          reconnectTimer = setTimeout(() => {
            if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) connect();
          }, 3000);
        };
      } catch {
        /* best-effort; REST stays canonical */
      }
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* noop */ }
        wsRef.current = null;
      }
    };
  }, [eventId, loading, error]);

  const href = viewAllHref || (eventId ? `/events/${eventId}/manage` : '/activity');

  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="activity-feed-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#111]">
        <div className="flex items-center gap-2 min-w-0">
          <ActivityIcon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
          <h3 className="text-sm font-semibold text-[#EAE7E1] tracking-tight truncate">
            {title}
            {!loading && !error && items.length > 0 && (
              <span className="text-[11px] text-[#777] font-normal ml-1.5">({items.length})</span>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!eventId && isOpen && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(href)}
              className="text-xs text-[#AFAFAF] hover:text-[#D4AF37] h-7 px-2"
              data-testid="activity-feed-view-all"
            >
              View all <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            className="text-xs text-[#AFAFAF] hover:text-[#D4AF37] h-7 px-2"
            data-testid="activity-feed-toggle"
          >
            {isOpen ? 'Hide' : 'Show'}
            {isOpen
              ? <ChevronUp className="w-3 h-3 ml-1" />
              : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
        </div>
      </div>

      {isOpen && (
        <>
          {loading && (
            <div className="px-4 py-8 text-center text-xs text-[#555]" data-testid="activity-feed-loading">
              Loading activity…
            </div>
          )}
          {!loading && error && (
            <div className="px-4 py-8 text-center text-xs text-[#C44545]" data-testid="activity-feed-error">
              {error}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="px-4 py-10 text-center" data-testid="activity-feed-empty">
              <p className="text-sm text-[#777]">No activity yet</p>
              <p className="text-[11px] text-[#555] mt-1">Ticket purchases, check-ins and other actions will appear here.</p>
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <div data-testid="activity-feed-list">
              {items.map((it) => (
                <ActivityFeedItem
                  key={it.id}
                  item={it}
                  showEventTitle={!eventId}
                  onNavigate={(link) => navigate(link)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
